import { useState } from "react";
import { DirectUpload } from "@rails/activestorage";

interface UploadOptions {
  directUploadUrl: string;
  onSuccess?: () => void;
  onError?: (errors: string[]) => void;
}

interface UploadProgress {
  [key: string]: number;
}

class DirectUploadController {
  file: File;
  fileKey: string;
  setFileProgress: React.Dispatch<React.SetStateAction<UploadProgress>>;
  calculateOverallProgress: (currentFileProgress: UploadProgress) => number;
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>;

  constructor(
    file: File,
    fileKey: string,
    setFileProgress: React.Dispatch<React.SetStateAction<UploadProgress>>,
    calculateOverallProgress: (currentFileProgress: UploadProgress) => number,
    setUploadProgress: React.Dispatch<React.SetStateAction<number>>,
  ) {
    this.file = file;
    this.fileKey = fileKey;
    this.setFileProgress = setFileProgress;
    this.calculateOverallProgress = calculateOverallProgress;
    this.setUploadProgress = setUploadProgress;
  }

  directUploadWillStoreFileWithXHR = (xhr: XMLHttpRequest) => {
    console.log(`File ${this.fileKey} upload starting`);

    // Initialize progress at 0%
    this.setFileProgress((prev) => {
      const newProgress = { ...prev, [this.fileKey]: 0 };
      const overallProgress = this.calculateOverallProgress(newProgress);
      this.setUploadProgress(overallProgress);
      return newProgress;
    });

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        // Map upload progress to 0-90% range to leave room for server processing
        const uploadPercent = Math.round((event.loaded / event.total) * 90);

        console.log(`File ${this.fileKey} upload progress: ${uploadPercent}%`);

        this.setFileProgress((prev) => {
          const newProgress = { ...prev, [this.fileKey]: uploadPercent };
          const overallProgress = this.calculateOverallProgress(newProgress);
          console.log(`Overall progress: ${overallProgress}%`);
          this.setUploadProgress(overallProgress);
          return newProgress;
        });
      }
    });

    xhr.upload.addEventListener("load", () => {
      console.log(
        `File ${this.fileKey} upload completed, starting server processing`,
      );
      // Set to 90% when upload finishes but before server processing
      this.setFileProgress((prev) => {
        const newProgress = { ...prev, [this.fileKey]: 90 };
        const overallProgress = this.calculateOverallProgress(newProgress);
        this.setUploadProgress(overallProgress);
        return newProgress;
      });
    });
  };
}

export const useVideoUpload = ({
  directUploadUrl,
  onSuccess,
  onError,
}: UploadOptions) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [fileProgress, setFileProgress] = useState<UploadProgress>({});

  const validateFile = (file: File): string | null => {
    const acceptedTypes = [
      "video/mp4",
      "video/mpeg",
      "video/quicktime",
      "video/x-msvideo",
      "video/webm",
    ];
    if (!acceptedTypes.includes(file.type)) {
      return "Please select a valid video file (MP4, MPEG, MOV, AVI, WebM)";
    }
    if (file.size > 10 * 1024 * 1024 * 1024) {
      return "File size must be less than 10GB";
    }
    return null;
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) {
      const errors = ["Please select at least one video file"];
      setUploadErrors(errors);
      onError?.(errors);
      return;
    }

    // Validate all files first
    const validationErrors: string[] = [];
    files.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        validationErrors.push(`${file.name}: ${error}`);
      }
    });

    if (validationErrors.length > 0) {
      setUploadErrors(validationErrors);
      onError?.(validationErrors);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadErrors([]);

    // Initialize progress for all files at 0%
    const initialProgress: UploadProgress = {};
    files.forEach((file, index) => {
      const fileKey = `${file.name}-${index}`;
      initialProgress[fileKey] = 0;
    });
    setFileProgress(initialProgress);

    try {
      const totalFiles = files.length;
      const uploadPromises: Promise<void>[] = [];

      // Function to calculate overall progress
      const calculateOverallProgress = (
        currentFileProgress: UploadProgress,
      ) => {
        const totalProgress = Object.values(currentFileProgress).reduce(
          (sum, p) => sum + p,
          0,
        );
        const maxProgress = totalFiles * 100;
        return Math.min(Math.round((totalProgress / maxProgress) * 100), 100);
      };

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileKey = `${file.name}-${i}`;

        const uploadPromise = new Promise<void>((resolve, reject) => {
          // Create the controller with all necessary dependencies
          const controller = new DirectUploadController(
            file,
            fileKey,
            setFileProgress,
            calculateOverallProgress,
            setUploadProgress,
          );

          // Pass the controller as the third parameter to DirectUpload
          const upload = new DirectUpload(file, directUploadUrl, controller);

          upload.create((error: { message: any }, blob: { signed_id: any }) => {
            if (error) {
              console.error(`Upload error for ${fileKey}:`, error);
              reject(new Error(`${file.name}: ${error.message}`));
            } else {
              console.log(
                `File ${fileKey} upload successful, creating video record`,
              );

              // Set to 95% when blob is created but before server processing
              setFileProgress((prev) => {
                const newProgress = { ...prev, [fileKey]: 95 };
                const overallProgress = calculateOverallProgress(newProgress);
                setUploadProgress(overallProgress);
                return newProgress;
              });

              // Submit each file as a separate video record
              fetch("/videos", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-CSRF-Token":
                    document
                      .querySelector('meta[name="csrf-token"]')
                      ?.getAttribute("content") || "",
                  "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({
                  video_file: blob.signed_id,
                }),
              })
                .then((response) => {
                  if (response.ok) {
                    console.log(`Video record created for ${fileKey}`);

                    // Mark this file as 100% complete
                    setFileProgress((prev) => {
                      const newProgress = { ...prev, [fileKey]: 100 };
                      const overallProgress =
                        calculateOverallProgress(newProgress);
                      setUploadProgress(overallProgress);
                      return newProgress;
                    });

                    resolve();
                  } else {
                    return response.text().then((text) => {
                      reject(
                        new Error(
                          `${file.name}: Failed to create video record - ${text}`,
                        ),
                      );
                    });
                  }
                })
                .catch((err) => {
                  reject(
                    new Error(`${file.name}: Network error - ${err.message}`),
                  );
                });
            }
          });
        });

        uploadPromises.push(uploadPromise);
      }

      await Promise.all(uploadPromises);
      setIsUploading(false);
      setUploadProgress(100);
      onSuccess?.();
    } catch (error) {
      setIsUploading(false);
      const errorMessages = [
        `Upload failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      ];
      setUploadErrors(errorMessages);
      onError?.(errorMessages);
    }
  };

  return {
    uploadProgress,
    isUploading,
    uploadErrors,
    fileProgress,
    uploadFiles,
    validateFile,
  };
};
